import { useEffect, useState, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import axios from "axios";
import { Check, ArrowRight, Warning, DownloadSimple, Copy, CheckCircle } from "@phosphor-icons/react";
import { useTranslation } from "react-i18next";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PaymentSuccessPage = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  const [status, setStatus] = useState("verifying");
  const [bookingData, setBookingData] = useState(null);
  const [copied, setCopied] = useState(false);

  const verifyPayment = useCallback(async () => {
    let attempts = 0;
    const maxAttempts = 10;
    const check = async () => {
      try {
        const res = await axios.get(`${API}/checkout/status/${sessionId}`);
        if (res.data.payment_status === "paid") {
          setBookingData(res.data);
          setStatus("confirmed");
        } else if (res.data.status === "expired") {
          setStatus("expired");
        } else {
          attempts++;
          if (attempts < maxAttempts) setTimeout(check, 2000);
          else setStatus("timeout");
        }
      } catch {
        setStatus("error");
      }
    };
    check();
  }, [sessionId]);

  useEffect(() => {
    if (sessionId) verifyPayment();
    else setStatus("error");
  }, [sessionId, verifyPayment]);

  if (status === "verifying") {
    return (
      <main className="pt-20 bg-[#EDEBE4] min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-2 border-[#1A1A1A]/20 border-t-[#1A1A1A] rounded-full animate-spin mx-auto mb-8" />
          <h1 className="font-heading text-2xl text-[#1A1A1A] mb-4">{t("payment.verifying")}</h1>
          <p className="text-[#1A1A1A]/70 font-light">{t("payment.wait")}</p>
        </div>
      </main>
    );
  }

  if (status === "confirmed" && bookingData) {
    const handleCopyWhatsapp = async () => {
      try {
        const res = await axios.get(`${API}/booking/${bookingData.booking_id}/whatsapp`);
        navigator.clipboard.writeText(res.data.text);
        setCopied(true);
        toast.success("Texte copie !");
        setTimeout(() => setCopied(false), 3000);
      } catch { toast.error("Erreur"); }
    };

    return (
      <main className="pt-20 bg-[#EDEBE4] min-h-screen" data-testid="payment-success">
        <div className="max-w-2xl mx-auto px-6 py-24 text-center">
          <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }}>
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-8">
              <Check size={40} weight="bold" className="text-green-600" />
            </div>
            <h1 className="font-heading text-3xl text-[#1A1A1A] mb-4">{t("payment.confirmed")}</h1>
            <p className="text-[#1A1A1A]/80 font-light mb-8">{t("payment.confirmed_desc")}</p>
            <div className="bg-white border border-[#1A1A1A]/10 p-8 mb-8 text-left max-w-md mx-auto space-y-4">
              <div>
                <p className="text-[#1A1A1A]/70 text-sm">{t("payment.ref")}</p>
                <p className="text-[#1A1A1A] font-mono">{bookingData.booking_id}</p>
              </div>
              <div>
                <p className="text-[#1A1A1A]/70 text-sm">{t("payment.amount")}</p>
                <p className="text-[#1A1A1A] font-heading text-2xl">{bookingData.amount_paid?.toFixed(2)}€</p>
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-4 max-w-md mx-auto mb-6">
              <a href={`${API}/booking/${bookingData.booking_id}/pdf`} target="_blank" rel="noopener noreferrer" className="btn-dark flex items-center justify-center gap-3" data-testid="download-pdf-btn">
                <DownloadSimple size={18} weight="bold" /> PDF
              </a>
              <button onClick={handleCopyWhatsapp} className="btn-primary flex items-center justify-center gap-3" data-testid="copy-whatsapp-btn">
                {copied ? <CheckCircle size={18} weight="bold" /> : <Copy size={18} weight="bold" />}
                {copied ? "Copie !" : "WhatsApp"}
              </button>
            </div>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/" className="text-[#1A1A1A]/60 text-sm hover:text-[#1A1A1A] transition-colors inline-flex items-center gap-2 justify-center">
                {t("payment.back_home")} <ArrowRight size={14} weight="bold" />
              </Link>
            </div>
          </motion.div>
        </div>
      </main>
    );
  }

  return (
    <main className="pt-20 bg-[#EDEBE4] min-h-screen" data-testid="payment-error">
      <div className="max-w-2xl mx-auto px-6 py-24 text-center">
        <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }}>
          <div className="w-20 h-20 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-8">
            <Warning size={40} weight="bold" className="text-orange-600" />
          </div>
          <h1 className="font-heading text-3xl text-[#1A1A1A] mb-4">
            {status === "timeout" ? t("payment.timeout") : t("payment.error")}
          </h1>
          <p className="text-[#1A1A1A]/80 font-light mb-8">
            {(() => {
              if (status === "timeout") return t("payment.timeout_desc");
              if (status === "expired") return t("payment.expired_desc");
              return t("payment.error_desc");
            })()}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/services/deplacements" className="btn-dark inline-flex items-center gap-3 justify-center">
              {t("payment.new_booking")}
              <ArrowRight size={16} weight="bold" />
            </Link>
            <Link to="/contact" className="btn-primary inline-flex items-center gap-3 justify-center">
              {t("payment.contact_us")}
            </Link>
          </div>
        </motion.div>
      </div>
    </main>
  );
};

export default PaymentSuccessPage;
